import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import * as propertyAmenityHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import * as rentRegistrationActions from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.actions";
import * as rentRegistrationSelectors from "@rentiohq/shared-frontend/dist/reduxV2/rentRegistration/rentRegistration.selectors";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import {
  ERegistrationDocumentType,
  IRegistration,
} from "@rentiohq/shared-frontend/dist/types/registration.types";
import {
  Error,
  Loading,
  MultiStepForm,
} from "@rentiohq/web-shared/dist/components";
import registrationAddDocumentForm from "forms/registrationAddDocument";
import {
  isPerformingSubmitActionSelector as isPerformingSubmitActionSelectorAddDocument,
  performingSubmitActionErrorSelector as performingSubmitActionErrorSelectorAddDocument,
  performingSubmitActionResultSelector as performingSubmitActionResultSelectorAddDocument,
  submitActionCreator as submitActionCreatorAddDocument,
} from "forms/registrationAddDocument/schema.registrationAddDocument.actions";
import { IExtraData as IExtraDataAddDocument } from "forms/registrationAddDocument/schema.registrationAddDocument.types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { NumberParam, createEnumParam } from "serialize-query-params";
import { ts } from "../../services";
import { isAddendumRegistrationPending } from "./RegistrationForm.utils";

export interface IProps {
  property: IProperty;
  contract: IContract;
  legalContact?: IContact;
}

export const AddendumRegistrationForm = (props: IProps) => {
  const { property, contract, legalContact } = props;

  const navigate = useNavigate();

  const [queryParams] = useQueryParams({
    id: NumberParam,
    documentType: createEnumParam(Object.values(ERegistrationDocumentType)),
    existingDocument: NumberParam,
  });

  const {
    id: registrationId,
    documentType = ERegistrationDocumentType.Addendum,
    existingDocument: existingDocumentId,
  } = queryParams;

  // Redux
  const dispatch = useDispatch();

  const { isBroker } = authHooks.useSelf();

  const [isLoading, setIsLoading] = React.useState(false);

  // Fetch property amenities
  const { items: propertyAmenities, isFetching: isFetchingPropertyAmenities } =
    propertyAmenityHooks.useGetByPropertyId({
      propertyId: property.id,
      shouldRefetch: true,
    });

  const contactMeMaster = useSelector((state: IRootStore) =>
    contactSelectors.getContactMeMaster(state),
  );

  const fetchError = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.fetchDetailError(state, registrationId),
  );

  const registration = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.getDetail(state, registrationId),
  );
  const isFetchingRegistration = useSelector((state: IRootStore) =>
    rentRegistrationSelectors.isFetchingDetail(state, registrationId),
  );

  const onSuccess = (registration: IRegistration) => {
    // The backend populates a freshly added document's barCode (and pageCount)
    // asynchronously, so an immediate refetch would still return a null
    // barCode. Wait for the backend before refetching the store the drawer
    // reads, so the barcode is shown without requiring a manual refresh.
    const isPending =
      isAddendumRegistrationPending(registration) ||
      documentType === ERegistrationDocumentType.PropertyInspectionReport;
    if (isPending) {
      fetchRegistrationWithTimeout(registration.id);
    } else {
      navigate(`/properties/${property.id}?registrationId=${registration.id}`);
    }
  };

  function fetchRegistration(id: number) {
    dispatch(
      rentRegistrationActions.getDetailStart.getAction({
        id,
        onSuccess: result =>
          result?.data &&
          navigate(
            `/properties/${property.id}?registrationId=${result.data.id}`,
          ),
      }),
    );
  }

  function fetchRegistrationWithTimeout(id: number) {
    setIsLoading(true);

    setTimeout(() => {
      fetchRegistration(id);
    }, 10000);
  }

  // Render
  if (fetchError) {
    return <Error errors={[fetchError]} />;
  }

  if (registration) {
    const extraData: IExtraDataAddDocument = {
      documentType,
      registrationId: registration.id,
      registration,
      existingDocumentId,
      contract,
      property,
      propertyAmenities,
      contactMeMaster,
      legalContact,
      isBroker,
    };

    if (isLoading || isFetchingRegistration || isFetchingPropertyAmenities) {
      return <Loading />;
    }

    return (
      <MultiStepForm
        formId={`registration-document-${property.id}-${contract.id}`}
        schemas={registrationAddDocumentForm(extraData)}
        withSummary={true}
        submitLabel={ts.system("save_send")}
        isPerformingSubmitActionSelector={isPerformingSubmitActionSelectorAddDocument(
          extraData,
        )}
        performingSubmitActionResultSelector={performingSubmitActionResultSelectorAddDocument(
          extraData,
        )}
        performingSubmitActionErrorSelector={performingSubmitActionErrorSelectorAddDocument(
          extraData,
        )}
        submitActionCreator={(formData: any) =>
          submitActionCreatorAddDocument(extraData)(formData)
        }
        onSuccess={onSuccess}
      />
    );
  }
};
